/**
 * Which message a refused voucher shows at checkout (#3332 D7, #3052). The
 * backend names why a found code may not be spent (`VoucherRefusalReason` in
 * the Lambda voucher service, `backend/src/core/modules/common/voucher/
 * voucherService.ts` — the other copy of this list; keep the two in step).
 * Anything else — above all a code that does not exist — stays the generic
 * "invalid coupon".
 *
 * The reason arrives in one of two places, depending on the handler:
 * `reason` at the top of the body (`POST reservations/{ids}/voucher`,
 * `POST commercial-model/{id}/voucher`) or under `details` (`processError`:
 * the room flow's `GET voucher/…` check and its later
 * `PATCH reservation/update-and-apply/{id}`).
 */
const REFUSAL_KEYS: Record<string, string> = {
  frozen: "book_location.error.voucher_frozen",
  expired: "book_location.error.voucher_expired",
  used_up: "book_location.error.voucher_used_up",
  refunded: "book_location.error.voucher_refunded",
  limit_reached: "book_location.error.voucher_limit_reached",
  no_payment: "book_location.error.voucher_no_payment",
  first_booking_only: "book_location.error.voucher_first_booking_only",
};

const NOT_VALID_HERE_KEY = "book_location.error.voucher_not_valid_here";
/** A scoped code whose stations could not be named (unnamed rows). */
const NOT_VALID_HERE_UNNAMED_KEY = "book_location.error.voucher_not_valid_here_unnamed";
export const INVALID_COUPON_KEY = "book_location.error.invalid_coupon";

/** The error body, whether axios parsed it or left the JSON string. */
const bodyOf = (error: unknown): unknown => {
  const data = (error as { data?: unknown } | null)?.data;
  if (typeof data !== "string") return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
};

type RefusalBody = { reason?: unknown; validAt?: unknown };

const refusalOf = (error: unknown): RefusalBody => {
  const body = bodyOf(error) as (RefusalBody & { details?: RefusalBody | null }) | null;
  if (!body || typeof body !== "object") return {};
  if (typeof body.reason === "string") return body;
  return body.details && typeof body.details === "object" ? body.details : {};
};

export interface VoucherRefusalMessage {
  key: string;
  /** Interpolation values for `t(key, params)`. */
  params?: Record<string, string>;
}

/** The message (key + interpolation) for a failed voucher request. */
export const voucherRefusalMessage = (error: unknown): VoucherRefusalMessage => {
  const { reason, validAt } = refusalOf(error);
  if (reason === "not_valid_here") {
    const places = Array.isArray(validAt)
      ? validAt.filter((p): p is string => typeof p === "string" && p.trim().length > 0)
      : [];
    return places.length
      ? { key: NOT_VALID_HERE_KEY, params: { places: places.join(", ") } }
      : { key: NOT_VALID_HERE_UNNAMED_KEY };
  }
  return { key: typeof reason === "string" && REFUSAL_KEYS[reason] ? REFUSAL_KEYS[reason] : INVALID_COUPON_KEY };
};

/** Every reason the app has its own message for — the test pins DE/EN for each. */
export const KNOWN_REFUSAL_REASONS = [...Object.keys(REFUSAL_KEYS), "not_valid_here"];
